import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { cloneDeep } from 'lodash';
import { filter, takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';

import { AberrationTabComponent } from '../aberration-tab/aberration-tab.component';
import { AberrationsFilter, FilterModes } from '../aberration-extra-details/aberration-extra-details-commons';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { CryptoService } from "@ispm/migration/services/crypto.service";
import { GenomeViewerService } from '../../../services/genome-viewer.service';

declare const igv: any;

@Component({
  selector: 'gen-igv',
  templateUrl: './igv.component.html',
  styleUrls: ['./igv.component.scss']
})
export class IgvComponent extends AberrationTabComponent implements OnInit {

  constructor(
    private authService: AuthenticationService ,
    private cryptoService: CryptoService,
    private genomeViewerService: GenomeViewerService,
  ) {
    super();
    this.loadScript();
  }

  loadScript() {
    const scriptElement = document.createElement('script');
    scriptElement.src = 'assets/isg/scripts/igv.min.js';
    scriptElement.type = 'text/javascript';
    scriptElement.onload = () => {
      this.igvIsLoaded.next(true);
    };
    const headElements = document.getElementsByTagName('head');
    headElements[0].appendChild(scriptElement);
  }

  readonly Variants = 'Variants';
  readonly AlignmentType = 'alignment';

  @ViewChild('igvDiv', { static: false, read: ElementRef}) igvDiv: ElementRef;
  @ViewChild('goToInput', {static: false}) goToSearchBox;

  private currentUser: any;
  private filterQueryString: string;
  private currentAberration: any;
  private distance: number = 100;
  private currentMode: FilterModes;
  private igvIsLoaded: Subject<boolean> = new ReplaySubject<boolean>();
  private variantsTrack = {
    name: this.Variants,
    type: 'annotation',
    sourceType: 'custom',
    color: '#3dc4ff',
    source: {
      url: '/genomeViewer/getJson/?chr=$CHR&start=$START&end=$END',
      method: 'GET',
      parser : JSON.parse,
      headers: {
        xsrf: '',
        aux_token: '',
        external_id: '',
        organization: '',
        revision_id: '',
        test_type_id: '',
        filter_query: '',
        current_modality: '',
        add_range: false,
        selected_ids: '',
        start: '',
        end: ''
      }
    }
  };

  selectedAberrationsArray: string[];
  popupContent: any = [];
  displayPopup: boolean = false;
  loading: boolean = true;
  tracksLoading: boolean = false;
  locus: string;
  availableTracks: ITrack[] = [];
  browser: any;
  options: any = {
    reference: { } ,
    locus: '',
    showCenterGuide: true,
    tracks: []
  };
  tracks: any[] =  [ ];

  static toTitleCase(txt: string) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  }

  generateTrackConfigName(label: string): string {
    return label.toLowerCase() + 'Track';
  }

  generateAlignmentTrack(name: string) {
    return {
      name: IgvComponent.toTitleCase(name),
      type: this.AlignmentType,
      format: 'bam',
      sourceType: 'file',
      colorBy: 'strand',
      samplingDepth: 3000,
      maxRows: 3000,
      alignmentRowHeight: 10,
      url: '',
      indexURL: '',
      headers: {
        xsrf : '',
        aux_token : '',
        auth_org_id : ''
      }
    };
  }

  ngOnInit(): void {
    this.currentUser = this.authService.user;
    this['config'] = this.testTableData.IGV;

    if (this.currentUser && this.currentUser.permissions) {
      this.currentUser.aux_token = this.cryptoService.hashSha256(JSON.stringify(this.currentUser.permissions.sort()) + '_' + (this.currentUser.roleId || ''));
    }

    if (this['config']) {
      this.availableTracks = cloneDeep(this['config'].tracks.filter((track: ITrack) => track.show)); // initialize drop down before UI

      this.availableTracks
        .filter((track: ITrack) => track.type === 'annotation')
        .forEach((track: ITrack) => {
          const trackConfigName = this.generateTrackConfigName(track.name);

          this[trackConfigName] = {
            name: track.label,
            type: track.type,
            sourceType: 'custom',
            displayMode: 'EXPANDED',
            expandQuery: false,
            color: '#0005ff',
            source: {
              url: `/${track.type}/igv?source=${track.name}&chr=$CHR&start=$START&end=$END`,
              method: 'GET',
              parser: JSON.parse,
              headers: {
                xsrf: '',
                aux_token: '',
                external_id: '',
                organization: '',
                revision_id: '',
                test_type_id: '',
                current_modality: '',
                source_name: track.name
              }
            }
          };
        });

      this.availableTracks
        .filter((track: ITrack) => track.type === this.AlignmentType)
        .forEach((track: ITrack) => {
          const trackConfigName = this.generateTrackConfigName(track.name);

          this[trackConfigName] = this.generateAlignmentTrack(track.label);
        });
    }

    Observable.combineLatest(
      this.aberration.takeUntil(this.destroy$).distinctUntilChanged().filter(aberration => !!aberration),
      this.selections,
      this.currentFilter.takeUntil(this.destroy$).distinctUntilChanged().filter(filter => !!filter)
    )
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(([aberration, selections, filter]) => {
        const isFirstTime = !this.currentAberration;
        this.selectedAberrationsArray = selections ? Object.keys(selections).filter(key => selections[key]) : [];
        this.setNewAberration(aberration);

        if (isFirstTime) {
          this.tracks = [];
          this.currentMode = filter.mode;
          this.filterQueryString = filter.filterQueryString;
          this.initView();
        }
        else {
          this.changeMode(filter);
        }
      });
  }

  initView(): void {
    if (this['config']) {
      this.extendCoverageTrackPopup();

      const ref: string = this['config'].reference.find(value => {
        return value.show;
      });

      forkJoin(
        this.genomeViewerService.getReferenceTrack(ref),
        this.genomeViewerService.getSpecificScanTrack(this.firstRevisionId)
      )
        .pipe(
          takeUntil(this.destroy$),
        )
      .subscribe(([ref, tracks]) => {
        this.options.reference = ref;
        this.initUrlTrackHeaderItems(this.filterQueryString);
        this.handleBamBaiTracks(tracks);
        this.initializeTracks();
        this.options.tracks = this.tracks;
        this.getIgvIsLoaded()
            .pipe(
              takeUntil(this.destroy$),
              filter( (data) => !!data)
            )
            .subscribe(() => {
              igv
                .createBrowser(this.igvDiv.nativeElement, this.options)
                .then((browser: any) => {
                  this.browser = browser;
                  this.loading = false;
                  this.tracksLoading = true;
                  this.browser.search(this.locus);
                  this.addBrowserEvents();
                });
          });
      });
    }
  }

  getIgvIsLoaded(): Observable<boolean> {
    return this.igvIsLoaded.asObservable();
  }

  initializeTracks() {
    this.availableTracks
      .filter((track: ITrack) => track.checked)
      .forEach((track: ITrack) => {
        const trackConfigName = this.generateTrackConfigName(track.name);
        const trackConfig = this[trackConfigName];

        if (track.label !== this.Variants && track.type !== this.AlignmentType) {
          this.initTrackHeaders(trackConfig.source.headers);
        }

        this.tracks.push(trackConfig);
      });
  }

  extendCoverageTrackPopup() {
    if (window['CoverageTrack']) {
      window['CoverageTrack'].prototype.popupData = function(config) {
        let genomicLocation = config.genomicLocation,
        referenceFrame = config.viewport.genomicState.referenceFrame,
        coverageMap = this.featureSource.alignmentContainer.coverageMap,
        coverageMapIndex: any,
        coverage: any,
        nameValues = [],
        nucleotides = ['A', 'C', 'G', 'T', 'N'];

        coverageMapIndex = genomicLocation - coverageMap.bpStart;
        coverage = coverageMap.coverage[coverageMapIndex];

        if (coverage) {
          nameValues.push(referenceFrame.chrName + ':' + igv.numberFormatter(1 + genomicLocation));
          nameValues.push({ name: 'Total Count', value: coverage.total});

          for (let i = 0; i < nucleotides.length; i++) {
            nameValues.push({ name: nucleotides[i], value: this.generateNucleotideData(coverage, nucleotides[i])});
          }
        }

        return nameValues;
      };

      window['CoverageTrack'].prototype.generateNucleotideData = function(coverage, nucleotide) {
        const posName = 'pos' + nucleotide;
        const negName = 'neg' + nucleotide;
        const tmp = coverage[posName] + coverage[negName];

        if (tmp > 0) {
          return tmp.toString() + ' (' + (tmp * 100.0 / coverage.total).toFixed(2) + '%, ' + coverage[posName] + '+, ' + coverage[negName] + '-)';
        }

        return tmp;
      };
    }

  }

  handleBamBaiTracks = (tracks: any[]) => {
    if (tracks.length > 0) {
      tracks.forEach(track => {
        let trackConfig: ITrack = this.availableTracks.find(availableTrack => track.name &&  track.name.indexOf(availableTrack.name) > -1);

        if (trackConfig) {
          const trackConfigName = this.generateTrackConfigName(trackConfig.name);

          if (track.type === 'bam') {
            this[trackConfigName].url = track.url;
          }

          if (track.type === 'bai') {
            this[trackConfigName].indexURL = track.url;
          }

          if (this.currentUser && this[trackConfigName].headers) {
            this[trackConfigName].headers.xsrf = this.currentUser.xsrf || '';
            this[trackConfigName].headers.aux_token = this.currentUser.aux_token || '';
            this[trackConfigName].headers.auth_org_id = this.currentUser.organization || '';
          }
        }
      });
    }
  }

  checkTrackViewExistence() {
    if (!(this.browser.trackViews && this.browser.trackViews.length > 0)) {
      setTimeout(() => {
        this.checkTrackViewExistence();
      }, 500);
    } else {
      this.availableTracks
        .filter((track: ITrack) => track.type === this.AlignmentType && track.checked)
        .forEach((track: ITrack) => {
          this.repaintTrackView(track.label);
        });
    }
  }

  repaintTrackView(trackId) {
    const trackView = this.findTrackView(trackId);
    this.waitTrackFinishLoading(trackView);
  }

  findTrackView(trackId) {
    const trackViews = this.browser.trackViews;
    let refTrackView: any;

    for (let i = 0; i < trackViews.length; i++) {
      const trackView = trackViews[i];

      if (trackView.track && trackView.track.id === trackId) {
        refTrackView = trackView;
        break;
      }
    }

    return refTrackView;
  }

  waitTrackFinishLoading(trackView) {
    if (!!trackView) {
      if (trackView.isLoading && trackView.isLoading()) {
        setTimeout(() => {
          this.waitTrackFinishLoading(trackView);
        }, 500);
      } else {
        if (trackView.resize && typeof trackView.resize == 'function') {
          trackView.resize();
        }
      }
    }
  }

  addBrowserEvents() {
    this.browser.on('locuschange', (referenceFrame: any[]) => {
      this.locus = referenceFrame[0].locusSearchString;
      this.initUrlTrackHeaderItems(this.filterQueryString);
      this.tracksLoading = false;
    });

    this.browser.on('trackclick' , (track, dataList) => {
      if (dataList && dataList.length > 0) {
        this.popupContent = dataList;
        this.displayPopup = true;
      }

      return false;
    });
  }

  setLocationFromAberration(aberration: any): string {
    let locus = '';

    if (aberration.coordinate) {
      const initialLocationArray: string[] = aberration.coordinate.split(':');
      const from = parseInt(initialLocationArray[1]) - this.distance;
      const to = parseInt(initialLocationArray[1]) + this.distance;
      locus = initialLocationArray[0] + ':' + new Intl.NumberFormat().format(from) + '-' + new Intl.NumberFormat().format(to);
    } else if (aberration.Start) {
      locus = aberration.Chrom + ':' + aberration.Start + '-' + aberration.End;
    } else if (aberration.start) {
      locus = aberration.chr + ':' + aberration.start + '-' + aberration.end;
    }

    return locus.replace('chr', '');
  }

  goTo() {
    if (this.browser) {
      let searchStr = this.locus.replace(/\s/g, '');

      if (searchStr.split('-').length > 2) {
        searchStr = searchStr.replace(/-/, ':');
      }

      this.tracksLoading = true;
      this.browser.search(searchStr);
    }
  }

  resetView() {
    this.setNewAberration(this.currentAberration);
    this.goToSearchBox.nativeElement.value = '';
  }

  setNewAberration(newAberration: any) {
    this.currentAberration = newAberration;
    this.updateRange();
    this.goTo();
  }

  updateRange() {
    if (this.currentAberration) {
      this.locus = this.setLocationFromAberration(this.currentAberration);
    }
  }

  changeMode(filter: AberrationsFilter) {
    if (filter.mode === this.currentMode) {
      return;
    }

    this.currentMode = filter.mode;
    this.filterQueryString = filter.filterQueryString;
    this.addOrRemoveTrack(this.Variants, false);

    if (this.currentMode === FilterModes.NotFiltered) {
      const chromosome = this.currentAberration.Chrom ? this.currentAberration.Chrom : this.currentAberration.chr;
      this.initUrlTrackHeaderItems('&chrom=' + chromosome);
    }
    else {
      this.initUrlTrackHeaderItems(this.filterQueryString);
    }

    this.addOrRemoveTrack(this.Variants, true);
  }

  initUrlTrackHeaderItems = (filterQuery: string) => {
    const headers = this.variantsTrack.source.headers;

    headers.external_id = this.currentUser.hsdpId || '';
    headers.xsrf = this.currentUser.xsrf || '';
    headers.aux_token =  this.currentUser.aux_token || '';
    headers.organization = this.currentUser.organization || '';
    headers.revision_id = this.firstRevisionId;
    headers.test_type_id = this.testTypeId;
    headers.current_modality = this.currentModality;
    headers.filter_query = filterQuery;
    headers.selected_ids = this.currentMode === FilterModes.Selected ? JSON.stringify(this.selectedAberrationsArray) : '';

    if (!!this.currentAberration.Start) {
      this.variantsTrack.source.headers.add_range = false;
      this.variantsTrack.source.headers.start = this.currentAberration.Start;
      this.variantsTrack.source.headers.end = this.currentAberration.End;
    } else {
      this.variantsTrack.source.headers.add_range = true;
    }
  }

  initTrackHeaders = (headers) => {
    headers.external_id = this.currentUser.hsdpId || '';
    headers.xsrf = this.currentUser.xsrf || '';
    headers.aux_token = this.currentUser.aux_token || '';
    headers.organization = this.currentUser.organization || '';
    headers.revision_id = this.firstRevisionId;
    headers.test_type_id = this.testTypeId;
    headers.current_modality = this.currentModality;
  }

  zoomIn() {
    this.browser.zoomIn();
  }

  zoomOut() {
    this.browser.zoomOut();
  }

  tracksChanged = (event) => {
    this.addOrRemoveTrack(event.name, event.checked);

    if (event.checked) {
      setTimeout(() => {
        const target = document.getElementsByClassName('igv-track-container-div')[0];
        target.scrollTop = Number.MAX_SAFE_INTEGER;
      }, 2000);
    }
  }

  addOrRemoveTrack = (name: string , isAdd: boolean) => {
    const track: ITrack = this.availableTracks.find((availableTrack: ITrack) => name &&  name.indexOf(availableTrack.name) > -1);

    if (!!track) {
      if (isAdd) {
        const trackConfig = this.getTrackByName(name);
        setTimeout((track) => {
          this.browser.loadTrack(track);
        }, 1, trackConfig);
      } else {
        const trackConfig = this[this.generateTrackConfigName(name)];

        if (trackConfig) {
          this.browser.removeTrackByName(trackConfig.name);
        }
      }
    }
  }

  getTrackByName = (name: string) => {
    const trackConfigName = this.generateTrackConfigName(name);

    if (this[trackConfigName] && this[trackConfigName].type !== this.AlignmentType && this[trackConfigName].name !== this.Variants) {
      this.initTrackHeaders(this[trackConfigName].source.headers);
    }

    return this[trackConfigName];
  }
}

interface ITrack {
  name: string;
  label: string;
  checked: boolean;
  show: boolean;
  type: string;
  url?: string;
}
